import * as React from "react"
import { Dialog, Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"

import { cn } from "@/utils"

export interface ModalProps {
  /** Whether the modal is visible */
  open: boolean
  /** Called when the modal should close */
  onClose: () => void
  /** Modal title */
  title?: React.ReactNode
  /** Footer content (typically action buttons) */
  footer?: React.ReactNode
  /** Additional className for the modal panel */
  className?: string
  /** Max width */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl"
  children: React.ReactNode
}

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
}

function Modal({
  open,
  onClose,
  title,
  footer,
  className,
  maxWidth = "lg",
  children,
}: ModalProps) {
  return (
    <Transition appear show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn(
                  "w-full rounded-xl bg-popover border border-border p-6 shadow-lg",
                  maxWidthClasses[maxWidth],
                  className
                )}
              >
                {/* Header */}
                {title && (
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title className="text-lg font-semibold text-foreground">
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {/* Body */}
                <div>{children}</div>

                {/* Footer */}
                {footer && (
                  <div className="flex items-center justify-end gap-2 mt-6">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { Modal }
